import { useMemo } from 'react'

import { cn } from '@/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useFontsOptional } from '@/providers/font'

import { FOCUS_RING_CLASS } from '../../../constants/focus-ring-class'
import { POPOVER_CLASS } from '../constants/format-control-classes'
import type { ControlProps } from '../types/control-props'

/**
 * The family's real variants, not four hardcoded Regular/Italic/Bold/Bold Italic entries — a given
 * family may have fewer, or name them differently (Oblique). Bold and italic are properties OF a
 * variant in this data model; there are no standalone toggles anywhere in the editor.
 */
export function TextOnPathFontStyleControl({
  config,
  onConfigChange,
  onRestoreFocus,
  className,
}: ControlProps) {
  const fonts = useFontsOptional()
  const variants = useMemo(() => {
    const list = fonts?.getFamilyById(config.fontFamilyId)?.fonts ?? []

    return [...list].sort(
      (a, b) => (a.fontStyle?.fontStyleOrder ?? 0) - (b.fontStyle?.fontStyleOrder ?? 0)
    )
  }, [fonts, config.fontFamilyId])
  const currentStyleName = fonts?.getVariantByFontId(config.fontId)?.fontStyleName

  return (
    <Select
      value={variants.length ? String(config.fontId) : ''}
      onValueChange={(next) => onConfigChange({ fontId: Number(next) })}
      disabled={variants.length === 0}
    >
      <SelectTrigger
        size="sm"
        aria-label="Font style"
        // `SelectTrigger` brings its own box (`rounded-md border-input shadow-xs`), so this has to
        // strip it explicitly rather than just add to it — the group box owns the border here.
        className={cn(
          'w-[6.75rem] flex-shrink-0 rounded-md border-none bg-transparent px-3 text-xs font-medium text-foreground shadow-none hover:bg-accent',
          FOCUS_RING_CLASS,
          className
        )}
      >
        <SelectValue placeholder={currentStyleName || 'Style'} />
      </SelectTrigger>
      <SelectContent
        className={POPOVER_CLASS}
        data-text-edit-keepalive=""
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          onRestoreFocus?.()
        }}
      >
        {variants.map((variant) => (
          <SelectItem key={variant.fontId} value={String(variant.fontId)} className="text-xs">
            {variant.fontStyle?.fontStyleName ?? variant.fontName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
